import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Hash,
  Megaphone,
  MessageSquare,
  MessagesSquare,
  Plus,
  UsersRound,
} from 'lucide-react';
import type { Me } from '../api/auth';
import { conversationsApi } from '../api/conversations';
import type { ConversationListRow, EventThreadPage, UnreadSummary } from '../api/conversations';
import { useLiveMessages } from '../hooks/useLiveMessages';
import { AppHeader } from '../ui/AppHeader';
import { Button } from '../ui/Button';
import { Footer, Gutter, Page } from '../ui/Chrome';
import { EmptyState } from '../ui/EmptyState';
import { EventThreadSection, ThreadSection } from '../ui/ThreadList';
import type { EventSort } from '../ui/ThreadList';
import { monthGroupsOf, toListRow } from '../lib/eventMonths';

/**
 * Which section of the page a thread belongs in.
 *
 * Derived from facts the row already carries — `anchor`, `audienceMode`, `audienceSubgroupId` —
 * rather than from a discriminator the server computed for this screen (§16). Nothing else in the
 * product needs to agree with this function, which is why it can live here.
 */
type SectionKey = 'channels' | 'groups' | 'threads' | 'events' | 'announcements' | 'direct';

function sectionOf(row: ConversationListRow): SectionKey {
  if (row.anchor === 'Event') return 'events';
  if (row.anchor === 'Announcement') return 'announcements';
  if (row.audienceMode === 'Explicit') return 'direct';

  // A whole-roster thread somebody started is not one of the unit's channels, and the difference is
  // not visible in its audience -- both are RosterScoped and anchored to nothing. It is a real and
  // ordinary thing to make ("Summer camp planning", for the whole troop), told apart by its name,
  // so it gets its own section rather than being hidden or muddled in with the standing few.
  if (!row.isStandingChannel) return 'threads';

  return row.audienceSubgroupId ? 'groups' : 'channels';
}


/**
 * Pages of the recency feed fetched when the screen opens.
 *
 * Three is enough to fill the event, announcement and direct sections for a real unit, and short of
 * what the dev troop's three thousand threads would need. The rest arrives on *Load more* rather
 * than in a hundred requests nobody asked for.
 */
const PAGES_ON_OPEN = 3;

export function ConversationsScreen({
  unitId,
  unitName,
  subgroupLabelPlural,
  canStartRosterThreads,
  me,
  onNavigate,
}: {
  unitId: string;
  unitName: string;
  /** The unit's own word — "Patrols" or "Dens". Never a literal on this screen (rule 16). */
  subgroupLabelPlural: string;
  canStartRosterThreads: boolean;
  me: Me;
  onNavigate: (path: string) => void;
}) {
  const live = useLiveMessages();

  const [channels, setChannels] = useState<ConversationListRow[] | null>(null);
  const [events, setEvents] = useState<EventThreadPage | null>(null);
  const [eventSort, setEventSort] = useState<EventSort>('date');
  const [movingMonth, setMovingMonth] = useState(false);
  const [feed, setFeed] = useState<ConversationListRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [unread, setUnread] = useState<UnreadSummary | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      // The channels and the feed are two calls because they are two different questions: the
      // channels are a set and the feed is ordered by recency. A channel nobody has posted in has
      // no last-message time, so it sorts behind every thread that anybody has ever posted in --
      // which on a unit's first day is every channel it owns.
      const [channelPage, eventPage, summary] = await Promise.all([
        conversationsApi.channels(unitId),
        conversationsApi.events(unitId),
        conversationsApi.unreadTotal(unitId),
      ]);

      const rows: ConversationListRow[] = [];
      let next: string | null = null;

      for (let page = 0; page < PAGES_ON_OPEN; page++) {
        const result = await conversationsApi.list(unitId, next ?? undefined);
        rows.push(...result.rows);
        next = result.nextCursor;
        if (!next) break;
      }

      setChannels(channelPage.rows);
      setEvents(eventPage);
      setFeed(rows);
      setCursor(next);
      setUnread(summary);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load conversations.');
    }
  }, [unitId]);

  useEffect(() => {
    void load();
  }, [load]);

  // A message landing anywhere this reader can see refreshes the list. The server decides who is
  // told (it addresses nudges to people, not conversations), so a thread nobody has open still
  // moves to the top and still lights its badge -- which is the whole reason this page was stale
  // before (TROOP-91).
  //
  // A full reload rather than patching the row: the sections, the counts and the badges all come
  // off one authorization pass, and hand-editing one row would leave five numbers describing
  // something that had changed underneath them.
  useEffect(() => {
    if (live.tick === 0) return;
    if (live.latest && live.latest.unitId !== unitId) return;

    void load();
  }, [live.tick, live.latest, unitId, load]);

  async function loadMore() {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await conversationsApi.list(unitId, cursor);
      setFeed((current) => [...current, ...result.rows]);
      setCursor(result.nextCursor);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load more.');
    } finally {
      setLoadingMore(false);
    }
  }

  const sections = useMemo(() => {
    const all = channels ?? [];

    return {
      channels: all.filter((r) => !r.audienceSubgroupId),
      groups: all.filter((r) => r.audienceSubgroupId),
      // The feed carries the standing channels too, since one somebody posted in today is near the
      // top of it. Those are drawn from the channels call above instead, so `sectionOf` sends them
      // to 'channels'/'groups' and nothing below picks them up -- rendered once, not twice.
      threads: feed.filter((r) => sectionOf(r) === 'threads'),
      announcements: feed.filter((r) => sectionOf(r) === 'announcements'),
      direct: feed.filter((r) => sectionOf(r) === 'direct'),
    };
  }, [channels, feed]);

  // Its own read, so nothing here comes off the feed -- an event nobody has posted about has no
  // recency and would sit three thousand rows down the cursor.
  const eventMonths = useMemo(() => monthGroupsOf(events?.rows ?? []), [events]);
  const recentEvents = useMemo(() => (events?.recent ?? []).map(toListRow), [events]);

  /**
   * Walking to another month is a fresh read, because the window is the server's to decide -- a
   * unit has thousands of events and the policy runs per row, so neither the slice nor the counts
   * can be worked out here.
   */
  async function moveTo(month: string) {
    setMovingMonth(true);
    try {
      setEvents(await conversationsApi.events(unitId, month));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load that month.');
    } finally {
      setMovingMonth(false);
    }
  }

  const open = (conversationId: string) =>
    onNavigate(`/units/${unitId}/conversations/${conversationId}`);

  const nothingAtAll =
    channels !== null &&
    sections.channels.length === 0 &&
    sections.groups.length === 0 &&
    sections.threads.length === 0 &&
    (events?.rows.length ?? 0) === 0 &&
    sections.announcements.length === 0 &&
    sections.direct.length === 0;

  return (
    <Page>
      <AppHeader me={me} currentUnitId={unitId} onNavigate={onNavigate} />

      <section className="rule-canonical">
        <Gutter>
          <div style={{ paddingBlock: 'var(--space-6)' }}>
            <p className="t-eyebrow" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
              {unitName}
            </p>
            <h1
              className="t-heading"
              style={{ color: 'var(--color-text-primary)', margin: 'var(--space-2) 0 var(--space-4)' }}
            >
              Conversations
            </h1>

          </div>
        </Gutter>
      </section>

      <Gutter>
        <div style={{ paddingBlock: 'var(--space-6)' }}>
          {error && (
            <p className="t-body" style={{ color: 'var(--color-status-blocked)' }}>
              {error}
            </p>
          )}

          {channels === null && !error && (
            <p className="t-body" style={{ color: 'var(--color-text-secondary)' }}>
              Loading&hellip;
            </p>
          )}

          {nothingAtAll && (
            <EmptyState
              icon={MessageSquare}
              headline="Nothing has been said yet"
              explanation="Your unit's channels appear here, along with the thread on every event and announcement."
            />
          )}

          {channels !== null && !nothingAtAll && (
            <div className="flex flex-col" style={{ gap: 'var(--space-6)' }}>
              <ThreadSection
                icon={Hash}
                title="Channels"
                rows={sections.channels}
                unread={unread?.byGroup.channels ?? 0}
                onOpen={open}
              />

              <ThreadSection
                icon={UsersRound}
                title={subgroupLabelPlural}
                rows={sections.groups}
                unread={unread?.byGroup.groups ?? 0}
                onOpen={open}
              />

              <EventThreadSection
                months={eventMonths}
                recent={recentEvents}
                index={events?.months ?? []}
                unread={unread?.byGroup.events ?? 0}
                sort={eventSort}
                busy={movingMonth}
                onSortChange={setEventSort}
                onAnchorChange={(month) => void moveTo(month)}
                onOpen={open}
              />

              <ThreadSection
                icon={Megaphone}
                title="Announcements"
                rows={sections.announcements}
                unread={unread?.byGroup.announcements ?? 0}
                partial={cursor !== null}
                onOpen={open}
              />

              <ThreadSection
                icon={MessageSquare}
                title="Direct"
                rows={sections.direct}
                unread={unread?.byGroup.direct ?? 0}
                partial={cursor !== null}
                onOpen={open}
              />

              {/* Last, below Direct. The channels and the events are what somebody came for and a
                  direct message is addressed to them by name; this is the long tail of threads
                  somebody started once, so it sorts after both rather than between them. */}
              <ThreadSection
                icon={MessagesSquare}
                title="Other threads"
                rows={sections.threads}
                unread={unread?.byGroup.otherThreads ?? 0}
                partial={cursor !== null}
                onOpen={open}
              />

              {/* At the foot of the list rather than above it: starting a thread is what you do
                  after failing to find the one you wanted, and a page whose first control is
                  "start a new one" invites a duplicate of the thread three rows down. */}
              <div style={{ paddingTop: 'var(--space-2)' }}>
                <Button
                  variant="secondary"
                  onClick={() => onNavigate(`/units/${unitId}/conversations/new`)}
                >
                  <Plus size={16} aria-hidden="true" />
                  {canStartRosterThreads ? 'Start a conversation' : 'Message someone'}
                </Button>
              </div>

              {cursor !== null && (
                <div className="flex flex-col" style={{ gap: 'var(--space-2)' }}>
                  <Button variant="secondary" onClick={() => void loadMore()}>
                    {loadingMore ? 'Loading…' : 'Load more threads'}
                  </Button>
                  {/* Said plainly rather than left for somebody to notice. The sections above are
                      built from what has been loaded, and this unit has more. */}
                  <span className="t-caption" style={{ color: 'var(--color-text-secondary)' }}>
                    Announcements and other threads fill in as you load. Your channels and events
                    are all here already.
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </Gutter>

      <Footer />
    </Page>
  );
}
